import { useContext, createContext } from 'react'
import useAuth from './useAuth'

// Contexto para restricciones de empresa
const CompanyRestrictionsContext = createContext()

// Hook personalizado para restricciones de empresa
export const useCompanyRestrictions = () => {
  const context = useContext(CompanyRestrictionsContext)
  if (!context) {
    throw new Error('useCompanyRestrictions debe usarse dentro de CompanyRestrictionsProvider')
  }
  return context
}

// Provider para restricciones de empresa
export const CompanyRestrictionsProvider = ({ children }) => {
  const { user } = useAuth()
  
  // Verificar si es usuario cliente
  const isClientUser = user?.role === 'cliente'
  
  // Verificar si es usuario de departamento de cuentas
  const isAccountsUser = user?.role === 'dto_cuentas'
  
  // Obtener el ID de la empresa del usuario (para clientes)
  const userCompanyId = user?.company_id || null
  
  // Obtener las empresas asignadas (para dto_cuentas)
  const userAssignedCompanies = user?.assigned_companies || []
  
  // Obtener el nombre de la empresa del usuario
  const userCompanyName = user?.company_name || 'tu empresa'
  
  // Función para verificar si puede acceder a datos de una empresa específica
  const canAccessCompanyData = (companyId) => {
    if (!isClientUser && !isAccountsUser) return true // Admin y trabajadores pueden acceder a todo
    
    if (isClientUser) {
      return companyId === userCompanyId
    }
    
    if (isAccountsUser) {
      return userAssignedCompanies.includes(companyId)
    }
    
    return false
  }
  
  // Función para obtener filtros de empresa para consultas
  const getCompanyFilter = () => {
    if (!isClientUser && !isAccountsUser) return {} // Sin restricciones para admin/trabajadores
    
    if (isClientUser) {
      return { company_id: userCompanyId }
    }
    
    if (isAccountsUser) {
      return { company_id: userAssignedCompanies }
    }
    
    return {}
  }
  
  // Función para verificar si puede ver ciertas funcionalidades
  const canViewFeature = (feature) => {
    const featurePermissions = {
      'all_companies': !isClientUser && !isAccountsUser,
      'user_management': !isClientUser && !isAccountsUser,
      'system_config': !isClientUser && !isAccountsUser,
      'company_switching': !isClientUser && !isAccountsUser,
      'admin_panel': !isClientUser && !isAccountsUser,
      'reports_all_companies': !isClientUser && !isAccountsUser,
      'dashboard_all_companies': !isClientUser && !isAccountsUser
    }
    
    return featurePermissions[feature] || false
  }
  
  // Función para obtener mensaje de restricción
  const getRestrictionMessage = (feature) => {
    if (!isClientUser && !isAccountsUser) return null
    
    if (isClientUser) {
      const messages = {
        'all_companies': `Solo puedes ver datos de ${userCompanyName}`,
        'user_management': 'No tienes permisos para gestionar usuarios',
        'system_config': 'No tienes permisos para configurar el sistema',
        'company_switching': 'No puedes cambiar de empresa',
        'admin_panel': 'No tienes acceso al panel de administración',
        'reports_all_companies': `Solo puedes ver reportes de ${userCompanyName}`,
        'dashboard_all_companies': `Solo puedes ver el dashboard de ${userCompanyName}`
      }
      return messages[feature] || 'Acceso restringido'
    }
    
    if (isAccountsUser) {
      const messages = {
        'all_companies': `Solo puedes ver datos de tus empresas asignadas`,
        'user_management': 'No tienes permisos para gestionar usuarios',
        'system_config': 'No tienes permisos para configurar el sistema',
        'company_switching': 'No puedes cambiar de empresa',
        'admin_panel': 'No tienes acceso al panel de administración',
        'reports_all_companies': `Solo puedes ver reportes de tus empresas asignadas`,
        'dashboard_all_companies': `Solo puedes ver el dashboard de tus empresas asignadas`
      }
      return messages[feature] || 'Acceso restringido'
    }
    
    return 'Acceso restringido'
  }
  
  const value = {
    isClientUser,
    isAccountsUser,
    userCompanyId,
    userAssignedCompanies,
    userCompanyName,
    canAccessCompanyData,
    getCompanyFilter,
    canViewFeature,
    getRestrictionMessage
  }
  
  return (
    <CompanyRestrictionsContext.Provider value={value}>
      {children}
    </CompanyRestrictionsContext.Provider>
  )
}

export default useCompanyRestrictions
